import { Trans, useLingui } from "@lingui/react/macro";
import { ChatMarkdown } from "@rakazo/chat-ui/web";
import type { Artifact, Bot, Routine, ScratchpadItem, ThreadMessage } from "@rakazo/contracts";
import {
  BOT_SETTINGS_DEFAULTS,
  botAbilityEnabled,
  isAttachmentImageMimeType,
} from "@rakazo/contracts";
import { factDisplayText, fileSizeParts, openWorkState, sortOpenWork } from "@rakazo/core";
import { BotAvatar, Button, Tabs, TabsList, TabsTrigger } from "@rakazo/ui-web";
import { useQuery } from "@tanstack/react-query";
import {
  FileText,
  Folder,
  Image as ImageIcon,
  Link2,
  Pin,
  Plus,
  SlidersHorizontal,
} from "lucide-react";
import type { ReactNode } from "react";
import { useMemo, useState } from "react";
import { copyableMessageText } from "../../lib/message-text";
import type { SpaceQueries } from "../../lib/query";
import type { Panel } from "../../lib/right-panel-state";
import { RoutineList } from "../RoutineEditor";
import { BotWebsiteSection } from "./website-setup";

/** The three tabs beside a bot's chat. */
export const BOT_PANEL_TABS = ["details", "library", "computer"] as const;
export type BotPanelTab = (typeof BOT_PANEL_TABS)[number];

export function isBotPanelTab(panel: Panel): panel is BotPanelTab {
  return (BOT_PANEL_TABS as readonly Panel[]).includes(panel);
}

export function BotPanelTabs({
  value,
  onChange,
}: {
  value: BotPanelTab;
  onChange: (tab: BotPanelTab) => void;
}) {
  const { t } = useLingui();
  const labels: Record<BotPanelTab, string> = {
    details: t`Details`,
    library: t`Library`,
    computer: t`Computer`,
  };
  return (
    <Tabs
      value={value}
      onValueChange={(next) => {
        if (isBotPanelTab(next as Panel)) onChange(next as BotPanelTab);
      }}
    >
      <TabsList data-testid="bot-panel-tabs">
        {BOT_PANEL_TABS.map((tab) => (
          <TabsTrigger key={tab} value={tab} data-bot-panel-tab={tab} className="px-3">
            {labels[tab]}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}

/** Midnight in the viewer's timezone, which is where "today" starts for them. */
export function startOfLocalDay(now = new Date()): string {
  return new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
}

function SectionTitle({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="mt-7 mb-2 flex items-center justify-between gap-3">
      <h3 className="text-label font-semibold uppercase tracking-wider text-muted-foreground">
        {children}
      </h3>
      {action}
    </div>
  );
}

export function BotDetailsPanel({
  bot,
  queries,
  routines,
  runningRoutineId,
  onConfigure,
  onCreateRoutine,
  onEditRoutine,
  onStopRun,
  onAddIntegration,
  onOpenMemory,
  onRoutinesChanged,
}: {
  bot: Bot;
  queries: SpaceQueries;
  routines: Routine[];
  runningRoutineId?: string;
  onConfigure: () => void;
  onCreateRoutine: () => void;
  onEditRoutine: (routine: Routine) => void;
  onStopRun: () => void;
  onAddIntegration: () => void;
  /** Opens the full memory view in settings. */
  onOpenMemory?: () => void;
  /** Reads the routines again after one changed here, such as turning on website upkeep. */
  onRoutinesChanged?: () => void;
}) {
  const { t, i18n } = useLingui();
  const [since] = useState(startOfLocalDay);
  const stats = useQuery({
    ...queries.stats.botToday.queryOptions({ input: { botId: bot.id, since } }),
    refetchInterval: 30_000,
  }).data;
  // The inbox's own list, so this count always matches what the inbox shows for this bot.
  const inbox = useQuery({
    ...queries.inbox.list.queryOptions(),
    refetchInterval: 15_000,
  }).data;
  const needsYou = inbox
    ? inbox.asks.filter((ask) => ask.botId === bot.id).length +
      (inbox.facts ?? []).filter((fact) => fact.botId === bot.id).length
    : undefined;
  const connections = useQuery(queries.connections.list.queryOptions()).data ?? [];
  const connected = connections.filter((row) => row.status === "connected");
  const zoneName = useQuery({
    ...queries.memoryZones.list.queryOptions(),
    enabled: Boolean(bot.memoryZoneId),
  }).data?.zones.find((zone) => zone.id === bot.memoryZoneId)?.name;
  const count = new Intl.NumberFormat(i18n.locale);
  const on = t`On`;
  const off = t`Off`;
  const disabledTools = bot.disabledBuiltinTools ?? [];
  const contextLabels = { short: t`Short`, standard: t`Standard`, long: t`Long` };
  const summary: Array<[string, string]> = [
    [
      t`Computer`,
      !botAbilityEnabled(disabledTools, "computer")
        ? off
        : bot.computerMode === "dedicated"
          ? t`Private`
          : t`Team`,
    ],
    [t`Browser`, botAbilityEnabled(disabledTools, "browser") ? on : off],
    [t`Notify`, bot.notifyOnFinish ? on : off],
    [t`Model`, bot.modelId ?? t`Default`],
    [t`Context`, contextLabels[bot.contextSize ?? BOT_SETTINGS_DEFAULTS.contextSize]],
    ...(zoneName ? ([[t`Memory zone`, zoneName]] as Array<[string, string]>) : []),
  ];
  return (
    <div data-testid="bot-details">
      <div className="flex flex-col items-center gap-2 pt-2">
        <BotAvatar
          avatarSet={bot.avatarSet}
          color={bot.color}
          identity={bot.id}
          size={64}
          status={bot.status}
          starLabel={bot.superBot ? t`Super Nexa Mate` : undefined}
        />
        <p className="max-w-full truncate text-[17px] font-medium text-foreground" dir="auto">
          {bot.name}
        </p>
        <Button variant="outline" className="w-full rounded-full" onClick={onConfigure}>
          <SlidersHorizontal aria-hidden="true" />
          <Trans>Configure</Trans>
        </Button>
      </div>

      <dl className="mt-5 grid grid-cols-2 divide-x divide-border rounded-xl border border-border">
        <div className="px-3.5 py-3">
          <dt className="text-label font-semibold uppercase tracking-wider text-muted-foreground">
            <Trans>Today</Trans>
          </dt>
          <dd
            className="mt-1 text-heading2 font-semibold tabular-nums text-foreground"
            data-testid="tool-calls"
          >
            {stats ? count.format(stats.toolCalls) : "—"}
          </dd>
          <dd className="text-label text-faint-foreground">
            <Trans>tool calls</Trans>
          </dd>
        </div>
        <div className="px-3.5 py-3">
          <dt className="text-label font-semibold uppercase tracking-wider text-muted-foreground">
            <Trans>Open</Trans>
          </dt>
          <dd
            className="mt-1 text-heading2 font-semibold tabular-nums text-foreground"
            data-testid="needs-you"
          >
            {needsYou === undefined ? "—" : count.format(needsYou)}
          </dd>
          <dd className="text-label text-faint-foreground">
            <Trans>need you</Trans>
          </dd>
        </div>
      </dl>

      <SectionTitle>
        <Trans>Settings</Trans>
      </SectionTitle>
      <dl className="divide-y divide-border rounded-xl border border-border text-[13.5px]">
        {summary.map(([label, value]) => (
          <div key={label} className="flex items-center justify-between gap-3 px-3.5 py-2.5">
            <dt className="text-muted-foreground">{label}</dt>
            <dd className="min-w-0 truncate text-foreground">{value}</dd>
          </div>
        ))}
      </dl>

      <SectionTitle>
        <Trans>Routines</Trans>
      </SectionTitle>
      <RoutineList
        routines={routines}
        runningRoutineId={runningRoutineId}
        onCreate={onCreateRoutine}
        onEdit={onEditRoutine}
        onStop={onStopRun}
      />

      {(bot.access ?? "owner") === "owner" ? (
        <BotWebsiteSection
          botId={bot.id}
          queries={queries}
          onRoutinesChanged={onRoutinesChanged}
          title={
            <SectionTitle>
              <Trans>Website</Trans>
            </SectionTitle>
          }
        />
      ) : null}

      <BotOpenWorkSection bot={bot} queries={queries} onShowAll={onConfigure} />

      {(bot.access ?? "owner") === "owner" ? (
        <BotMemorySection bot={bot} queries={queries} onOpenAll={onOpenMemory} />
      ) : null}

      <SectionTitle>
        <Trans>Connected tools</Trans>
      </SectionTitle>
      <ul className="divide-y divide-border rounded-xl border border-border text-[13.5px]">
        {connected.map((row) => (
          <li key={row.id} className="flex items-center justify-between gap-3 px-3.5 py-2.5">
            <span className="min-w-0 truncate text-foreground">{row.displayName}</span>
            <span
              data-testid="connected-tool-dot"
              aria-hidden="true"
              className="size-2 shrink-0 rounded-full bg-success"
            />
          </li>
        ))}
        <li>
          <button
            type="button"
            onClick={onAddIntegration}
            className="flex w-full items-center gap-2 px-3.5 py-2.5 text-start text-muted-foreground hover:text-foreground"
          >
            <Plus size={14} aria-hidden="true" />
            <Trans>Add integration</Trans>
          </button>
        </li>
      </ul>
    </div>
  );
}

/** How many open work items the panel shows; the full list is in the bot's settings. */
export const OPEN_WORK_PREVIEW_COUNT = 4;

const NO_OPEN_WORK: ScratchpadItem[] = [];

/** The bot's open work, late and soonest first, with due times; read-only. */
function BotOpenWorkSection({
  bot,
  queries,
  onShowAll,
}: {
  bot: Bot;
  queries: SpaceQueries;
  onShowAll: () => void;
}) {
  const { t, i18n } = useLingui();
  const items =
    useQuery({
      ...queries.scratchpad.list.queryOptions({ input: { botId: bot.id, discovery: false } }),
      refetchInterval: 60_000,
    }).data ?? NO_OPEN_WORK;
  const now = new Date();
  const sorted = sortOpenWork(items);
  if (sorted.length === 0) return null;
  const due = new Intl.DateTimeFormat(i18n.locale, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
  const more = sorted.length - OPEN_WORK_PREVIEW_COUNT;
  return (
    <>
      <SectionTitle>
        <Trans>Open work</Trans>
      </SectionTitle>
      <ul
        data-testid="bot-open-work"
        className="divide-y divide-border rounded-xl border border-border text-[13.5px]"
      >
        {sorted.slice(0, OPEN_WORK_PREVIEW_COUNT).map((item) => {
          const state = openWorkState(item.dueAt, now);
          return (
            <li
              key={item.id}
              data-open-work-state={state}
              className="flex items-baseline justify-between gap-3 px-3.5 py-2.5"
            >
              <span
                className={`min-w-0 flex-1 truncate ${item.status === "parked" ? "text-muted-foreground" : "text-foreground"}`}
                dir="auto"
              >
                {item.title}
              </span>
              {item.dueAt ? (
                <time
                  dateTime={item.dueAt}
                  className={`shrink-0 text-label tabular-nums ${
                    state === "overdue"
                      ? "font-medium text-destructive"
                      : state === "due_soon"
                        ? "text-warning"
                        : "text-faint-foreground"
                  }`}
                >
                  {state === "overdue" ? <span className="sr-only">{t`Overdue`} </span> : null}
                  {due.format(new Date(item.dueAt))}
                </time>
              ) : null}
            </li>
          );
        })}
        {more > 0 ? (
          <li>
            <button
              type="button"
              onClick={onShowAll}
              className="w-full px-3.5 py-2.5 text-start text-muted-foreground hover:text-foreground"
            >
              {t`${more} more`}
            </button>
          </li>
        ) : null}
      </ul>
    </>
  );
}

/** How many memory rows show before "Show more". */
export const MEMORY_PREVIEW_COUNT = 3;

/**
 * What the bot is still learning (its open discovery items, with what it knows so far) or, once
 * there is nothing left to learn, its pinned notes and latest memories: its own, its zone's and
 * the Company zone's, each zone note labelled. Read-only and only for its owner; facts show
 * without the auto-save provenance capture appends. The full view is in settings.
 */
export function BotMemorySection({
  bot,
  queries,
  onOpenAll,
}: {
  bot: Bot;
  queries: SpaceQueries;
  onOpenAll?: () => void;
}) {
  const { t } = useLingui();
  const [expanded, setExpanded] = useState(false);
  const discovery = useQuery(
    queries.scratchpad.list.queryOptions({ input: { botId: bot.id, discovery: true } }),
  ).data;
  const learning = (discovery ?? NO_OPEN_WORK).filter((item) => item.status !== "done");
  const overview = useQuery({
    ...queries.memory.botOverview.queryOptions({ input: { botId: bot.id } }),
    enabled: discovery !== undefined && learning.length === 0,
  }).data;
  const rows: Array<{
    key: string;
    kind: "discovery" | "pinned" | "latest";
    text: string;
    known?: string;
    zone?: string | null;
  }> = learning.length
    ? learning.map((item) => ({
        key: item.id,
        kind: "discovery",
        text: item.title,
        known: item.known.trim(),
      }))
    : [
        ...(overview?.pinned ?? []).map((item) => ({ ...item, kind: "pinned" as const })),
        ...(overview?.latest ?? []).map((item) => ({ ...item, kind: "latest" as const })),
      ].map((item) => ({ ...item, text: factDisplayText(item.text) }));
  if (rows.length === 0) return null;
  const shown = expanded ? rows : rows.slice(0, MEMORY_PREVIEW_COUNT);
  return (
    <>
      <SectionTitle
        action={
          onOpenAll ? (
            <Button variant="text" size="xs" className="h-auto px-0" onClick={onOpenAll}>
              <Trans>View all</Trans>
            </Button>
          ) : undefined
        }
      >
        <Trans>Memory</Trans>
      </SectionTitle>
      <ul
        data-testid="bot-memory"
        className="divide-y divide-border rounded-xl border border-border text-[13.5px]"
      >
        {shown.map((row) => (
          <li
            key={`${row.kind}:${row.key}`}
            data-memory-kind={row.kind}
            className="flex items-start gap-2 px-3.5 py-2.5"
          >
            {row.kind === "pinned" ? (
              <Pin
                size={13}
                aria-label={t`Pinned`}
                className="mt-1 shrink-0 text-muted-foreground"
              />
            ) : null}
            <div className="min-w-0 flex-1 break-words">
              <div className="text-foreground" dir="auto">
                {row.text}
              </div>
              {row.known ? (
                <div className="mt-0.5 text-label text-muted-foreground" dir="auto">
                  <Trans>Known: {row.known}</Trans>
                </div>
              ) : null}
            </div>
            {row.zone ? (
              <span className="shrink-0 text-label text-faint-foreground">{row.zone}</span>
            ) : null}
          </li>
        ))}
        {rows.length > MEMORY_PREVIEW_COUNT ? (
          <li>
            <button
              type="button"
              aria-expanded={expanded}
              onClick={() => setExpanded((value) => !value)}
              className="w-full px-3.5 py-2.5 text-start text-muted-foreground hover:text-foreground"
            >
              {expanded ? <Trans>Show less</Trans> : <Trans>Show more</Trans>}
            </button>
          </li>
        ) : null}
      </ul>
    </>
  );
}

const LIBRARY_FILTERS = ["all", "photos", "docs", "links", "artifacts"] as const;
type LibraryFilter = (typeof LIBRARY_FILTERS)[number];
type LibraryItem =
  | { kind: "file"; key: string; createdAt: string; artifact: Artifact }
  | { kind: "link"; key: string; createdAt: string; url: string };

const URL_PATTERN = /https?:\/\/[^\s<>()[\]"'`]+[^\s<>()[\]"'`.,;:!?]/g;

/**
 * Photos are images; docs are every other file that is not audio or video; artifacts are the
 * files the bot made in a run, as opposed to ones people attached.
 */
export function libraryFileMatches(
  artifact: Pick<Artifact, "mimeType" | "runId">,
  filter: "photos" | "docs" | "artifacts",
): boolean {
  if (filter === "artifacts") return artifact.runId !== null;
  const image = isAttachmentImageMimeType(artifact.mimeType);
  if (filter === "photos") return image;
  return !image && !/^(audio|video)\//.test(artifact.mimeType);
}

/** Every distinct web link in the loaded messages, newest first. */
export function libraryLinks(messages: readonly ThreadMessage[]): LibraryItem[] {
  const seen = new Set<string>();
  const links: LibraryItem[] = [];
  for (const message of [...messages].reverse()) {
    for (const url of copyableMessageText(message).match(URL_PATTERN) ?? []) {
      if (seen.has(url)) continue;
      seen.add(url);
      links.push({ kind: "link", key: `link:${url}`, createdAt: message.createdAt, url });
    }
  }
  return links;
}

/** Splits items into this week (the last seven days) and older, newest first in each. */
export function groupByWeek<T extends { createdAt: string }>(
  items: readonly T[],
  now = new Date(),
) {
  const weekAgo = now.getTime() - 7 * 24 * 60 * 60 * 1000;
  const sorted = [...items].sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
  return {
    thisWeek: sorted.filter((item) => Date.parse(item.createdAt) >= weekAgo),
    older: sorted.filter((item) => Date.parse(item.createdAt) < weekAgo),
  };
}

function linkLabel(url: string): string {
  try {
    const parsed = new URL(url);
    return `${parsed.host}${parsed.pathname === "/" ? "" : parsed.pathname}`.replace(
      /[[\]()]/g,
      "",
    );
  } catch {
    return url;
  }
}

/** One entry per file name: a document saved again shows its newest version. */
function latestVersions(artifacts: readonly Artifact[]): Artifact[] {
  const newest = new Map<string, Artifact>();
  for (const artifact of artifacts) {
    const seen = newest.get(artifact.name);
    if (!seen || artifact.version > seen.version) newest.set(artifact.name, artifact);
  }
  return [...newest.values()];
}

export function BotLibraryPanel({
  bot,
  queries,
  messages,
  onOpenArtifact,
  onBrowseComputer,
}: {
  bot: Bot;
  queries: SpaceQueries;
  /** The chat's loaded messages, where shared links come from. */
  messages: readonly ThreadMessage[];
  onOpenArtifact: (artifact: Artifact) => void;
  onBrowseComputer: () => void;
}) {
  const { t, i18n } = useLingui();
  const [filter, setFilter] = useState<LibraryFilter>("all");
  const artifacts = useQuery(
    queries.artifacts.list.queryOptions({ input: { botId: bot.id } }),
  ).data;
  const files = useMemo<LibraryItem[]>(
    () =>
      latestVersions(artifacts ?? []).map((artifact) => ({
        kind: "file",
        key: `file:${artifact.id}`,
        createdAt: artifact.createdAt,
        artifact,
      })),
    [artifacts],
  );
  const links = useMemo(() => libraryLinks(messages), [messages]);
  const bytes = (artifacts ?? []).reduce((total, artifact) => total + artifact.size, 0);
  const size = (value: number) => {
    const parts = fileSizeParts(value, i18n.locale);
    return parts ? `${parts.amount} ${parts.unit}` : "";
  };
  const shown =
    filter === "links"
      ? links
      : filter === "all"
        ? [...files, ...links]
        : files.filter((item) => item.kind === "file" && libraryFileMatches(item.artifact, filter));
  const { thisWeek, older } = groupByWeek(shown);
  const filterLabels: Record<LibraryFilter, string> = {
    all: t`All`,
    photos: t`Photos`,
    docs: t`Docs`,
    links: t`Links`,
    artifacts: t`Artifacts`,
  };

  function renderItem(item: LibraryItem) {
    if (item.kind === "link") {
      return (
        <li key={item.key} className="flex min-w-0 items-center gap-3 px-3.5 py-2.5">
          <Link2 size={15} className="shrink-0 text-muted-foreground" aria-hidden="true" />
          <div className="min-w-0 flex-1 truncate text-[13.5px] [&_p]:m-0 [&_p]:truncate">
            <ChatMarkdown>{`[${linkLabel(item.url)}](${item.url})`}</ChatMarkdown>
          </div>
        </li>
      );
    }
    const Icon = isAttachmentImageMimeType(item.artifact.mimeType) ? ImageIcon : FileText;
    return (
      <li key={item.key}>
        <button
          type="button"
          onClick={() => onOpenArtifact(item.artifact)}
          className="flex w-full min-w-0 items-center gap-3 px-3.5 py-2.5 text-start hover:bg-accent"
        >
          <Icon size={15} className="shrink-0 text-muted-foreground" aria-hidden="true" />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[13.5px] text-foreground" dir="auto">
              {item.artifact.name}
            </span>
            <span className="block text-label text-faint-foreground">
              {size(item.artifact.size)}
            </span>
          </span>
        </button>
      </li>
    );
  }

  return (
    <div data-testid="bot-library">
      <p className="rounded-xl border border-border px-3.5 py-2.5 text-[13.5px] text-foreground">
        {t`Storage · ${size(bytes)}`}
      </p>
      {/* biome-ignore lint/a11y/useSemanticElements: a row of toggle buttons, not a form fieldset. */}
      <div role="group" aria-label={t`Filter library`} className="mt-3 flex flex-wrap gap-1">
        {LIBRARY_FILTERS.map((option) => (
          <Button
            key={option}
            size="xs"
            variant={filter === option ? "default" : "ghost"}
            aria-pressed={filter === option}
            data-library-filter={option}
            onClick={() => setFilter(option)}
            className="rounded-full px-2.5"
          >
            {filterLabels[option]}
          </Button>
        ))}
      </div>
      <Button variant="outline" className="mt-3 w-full justify-start" onClick={onBrowseComputer}>
        <Folder aria-hidden="true" />
        <Trans>Browse computer</Trans>
      </Button>
      {thisWeek.length > 0 ? (
        <>
          <SectionTitle>
            <Trans>This week</Trans>
          </SectionTitle>
          <ul
            data-library-group="week"
            className="divide-y divide-border rounded-xl border border-border"
          >
            {thisWeek.map(renderItem)}
          </ul>
        </>
      ) : null}
      {older.length > 0 ? (
        <>
          <SectionTitle>
            <Trans>Older</Trans>
          </SectionTitle>
          <ul
            data-library-group="older"
            className="divide-y divide-border rounded-xl border border-border"
          >
            {older.map(renderItem)}
          </ul>
        </>
      ) : null}
    </div>
  );
}
